import { CLASS_GROUPS } from "#constants/classOptions"
import { CustomerSkillLevelLabel } from "#constants/skillLevelConstants"
import { EnumValues } from "#customTypes/types"

// Structured fee breakdown for enrollment receipts
export type ReceiptFeeBreakdown = {
  tuitionPerSession: number
  tuitionTotal: number
  tuitionAfterDiscount: number
  suppliesFee: number
  materialsFee: number
  discountAmount: number
  discountType: "SIBLING" | "EXTRA_CLASS" | null
  grandTotal: number
  sessionCount: number
  usingSemesterRate: boolean
}

export type AddStudentToClassReceipt = {
  recipients: EmailRecipient[]
  sendTime: string

  // The fields below must match to the fields in the receipt email template
  studentName: string
  DOB: string // TODO: rename to studentDob (left over from v1)
  studentSkill: CustomerSkillLevelLabel

  className: string
  numSessions: number
  startDate: string
  endDate: string

  optOutDatesLabel: string
  optOutDates: string
  paymentAmount: string // Grand total as formatted string (e.g., "$605")
  siblingDiscount: boolean
  extraClassDiscount: boolean
}

export type SwitchClassReceipt = {
  recipients: EmailRecipient[]
  sendTime: string

  // The fields below must match to the fields in the receipt email template
  DOB: string
  Name: string
  NewClassDay: string
  NewClassEndTime: string
  NewClassFirstDate: string
  NewClassLastDate: string
  NewClassStartTime: string
  OldClassDay: string
  OldClassEndTime: string
  OldClassLastDate: string
  OldClassStartTime: string
}

export type AddTryoutReceipt = {
  recipients: EmailRecipient[]
  sendTime: string

  // The fields below must match to the fields in the receipt email template
  studentName: string
  studentDob: string

  sessionDate: string
  sessionDay: string
  sessionStartTime: string
  sessionEndTime: string

  tryoutFees: string
  isTryoutFeePaid: boolean
}

export type TryoutFollowupPassReceipt = {
  recipients: EmailRecipient[]
  sendTime: string
  // Optional ISO timestamp for scheduled delivery via Mandrill `send_at`.
  // Server falls back to immediate send when omitted or in the past.
  sendAt?: string

  studentFirstName: string
  studentLastName: string
  startDate: string
  classGroup: CLASS_GROUPS
  prettyClassName: string
  startTime: string
  endTime: string
  skillLevel: CustomerSkillLevelLabel
  semester: string
  sessionCount: number
  firstSessionDate: string
  lastSessionDate: string
  tuition: string
  materialsCost: string
  total: string
}

export type AddMakeupReceipt = {
  recipients: EmailRecipient[]
  sendTime: string

  // The fields below must match to the fields in the receipt email template
  DOB: string
  Name: string
  ZoomLink: string | null

  ClassDate: string
  ClassDay: string
  ClassEndTime: string
  ClassStartTime: string

  MakeupClassDate: string | null
  MakeupClassDay: string | null
  MakeupClassEndTime: string | null
  MakeupClassStartTime: string | null
}

export enum EMAIL_RECIPIENT_TYPE {
  TO = "to",
  CC = "cc",
  BCC = "bcc",
}

export type EmailRecipient = {
  name: string
  email: string
  type: EMAIL_RECIPIENT_TYPE
}

// Mandrill attachment shape: base64 `content`, MIME `type`, and the file
// `name` the recipient sees. Mirrors StorytimeAttachment on the server.
export type EmailAttachment = {
  type: string
  name: string
  content: string
}

export type StorytimeEmailRequest = {
  recipients: EmailRecipient[]
  attachments: EmailAttachment[]
  // Spliced into the STORYTIME template when it has a |ClassName| placeholder.
  className?: string
}

export const STORYTIME_ENDPOINT = `/api/sendStorytime`

export const ENROLLMENT_RECEIPT_TYPES = {
  ENROLLMENT_NEW: "ENROLLMENT_NEW",
  ENROLLMENT_RENEWAL: "ENROLLMENT_RENEWAL",
  SWITCH_CLASS: "SWITCH_CLASS",
} as const

export const MAKEUP_RECEIPT_TYPES = {
  MAKEUP_CONFIRMATION: "MAKEUP_CONFIRMATION",
  MAKEUP_CANCELLATION: "MAKEUP_CANCELLATION",
  MAKEUP_CHANGE: "MAKEUP_CHANGE",
  MAKEUP_TBD: "MAKEUP_TBD",
  VACATION_ACKNOWLEDGEMENT: "VACATION_ACKNOWLEDGEMENT",
} as const

export const TRYOUT_RECEIPT_TYPES = {
  TRYOUT_CONFIRMATION: "TRYOUT_CONFIRMATION",
  TRYOUT_CANCELLATION: "TRYOUT_CANCELLATION",
  TRYOUT_CHANGE: "TRYOUT_CHANGE",
  TRYOUT_FOLLOWUP_PASS: "TRYOUT_FOLLOWUP_PASS",
  // TRYOUT_FOLLOWUP_FAIL: "TRYOUT_FOLLOWUP_FAIL",
} as const

export type EnrollmentReceiptType = EnumValues<typeof ENROLLMENT_RECEIPT_TYPES>
export type MakeupReceiptType = EnumValues<typeof MAKEUP_RECEIPT_TYPES>
export type TryoutReceiptType = EnumValues<typeof TRYOUT_RECEIPT_TYPES>

export const RECEIPT_ENDPOINTS = {
  [ENROLLMENT_RECEIPT_TYPES.ENROLLMENT_NEW]: `/api/sendReceipt/enrollment/new`,
  [ENROLLMENT_RECEIPT_TYPES.ENROLLMENT_RENEWAL]: `/api/sendReceipt/enrollment/renewal`,
  [ENROLLMENT_RECEIPT_TYPES.SWITCH_CLASS]: `/api/sendReceipt/enrollment/switchClass`,

  [MAKEUP_RECEIPT_TYPES.MAKEUP_CONFIRMATION]: `/api/sendReceipt/makeup/confirmation`,
  [MAKEUP_RECEIPT_TYPES.MAKEUP_CANCELLATION]: `/api/sendReceipt/makeup/cancellation`,
  [MAKEUP_RECEIPT_TYPES.MAKEUP_CHANGE]: `/api/sendReceipt/makeup/change`,
  [MAKEUP_RECEIPT_TYPES.MAKEUP_TBD]: `/api/sendReceipt/makeup/tbd`,
  [MAKEUP_RECEIPT_TYPES.VACATION_ACKNOWLEDGEMENT]: `/api/sendReceipt/makeup/vacationAcknowledgement`,

  [TRYOUT_RECEIPT_TYPES.TRYOUT_CONFIRMATION]: `/api/sendReceipt/tryout/confirmation`,
  [TRYOUT_RECEIPT_TYPES.TRYOUT_CANCELLATION]: `/api/sendReceipt/tryout/cancellation`,
  [TRYOUT_RECEIPT_TYPES.TRYOUT_CHANGE]: `/api/sendReceipt/tryout/change`,
  [TRYOUT_RECEIPT_TYPES.TRYOUT_FOLLOWUP_PASS]: `/api/sendReceipt/tryout/followupPass`,
  // [TRYOUT_RECEIPT_TYPES.TRYOUT_FOLLOWUP_FAIL]: `/api/sendReceipt/tryout/followupFail`,
}
